import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { Company } from '@models/company.model';
import { FileItem } from '@models/file-item.model';
import { PurchaseOrderDto, PurchaseOrderSubscriberModel } from '@app/models/purchase-order.model.dto';
import { CompanyService } from '@services/company.service';
import { AppPurchaseOrderService } from '@services/purchase-order.service';
import { Toaster } from '@services/toaster.service';
import * as moment from 'moment';
import { LocalStorage } from 'ngx-webstorage';
import Swal from 'sweetalert2';
import { CreateCompanyModalComponent } from '@shared/modals';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '@env/environment';

@Component({
  selector: 'ngx-purchase-order-creation',
  templateUrl: './purchase-order-creation.component.html',
  styleUrls: ['./purchase-order-creation.component.scss'],
})
export class PurchaseOrderCreationComponent implements OnInit {
  isRCDSS = environment.name === 'rcdss';
  @LocalStorage() organizationId;

  selectedTabIndex = 0;
  purchaseOrderForm: FormGroup;
  purchaseOrder: PurchaseOrderDto;
  poFiles: FileItem[] = [];
  subscribers: PurchaseOrderSubscriberModel[] = [];
  checklist: any;
  suppliers: Company[];

  isSaving: boolean;
  isDraft: boolean;

  canSupplierReceivePos = true;

  @ViewChild('stepper', { static: true }) myStepper: MatStepper;

  constructor(private formBuilder: FormBuilder,
              private companyService: CompanyService,
              private purchaseOrderService: AppPurchaseOrderService,
              private $toaster: Toaster,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              private location: Location,
  ) { }

  async ngOnInit() {
    this.purchaseOrderForm = this.formBuilder.group({
      poInfo: this.formBuilder.group({
        customer_po_number: [null, [Validators.required]],
        supplier_id: [null, [Validators.required]],
        sales_order_number: [],
        order_date: [moment.utc().startOf('d'), [Validators.required]],
        customer_priority: [2, [Validators.required]],
        ship_date: [null, [Validators.required]],
        report_start_date: [moment.utc().startOf('d'), [Validators.required]],
        cancel_date: [null, [Validators.required]],
        finished_goods_reports_period: ['daily', [Validators.required]],
        approver_id: [],
        line_items: new FormArray([], [Validators.minLength(1)]),
      }),
    });

    this.companyService.suppliers(this.organizationId)
      .subscribe((suppliers) => {
        this.suppliers = suppliers;
      });

    if (this.activatedRoute.snapshot.queryParams.draftId) {
      this.isDraft = true;
      this.getPoDrag(this.activatedRoute.snapshot.queryParams.draftId);
    }
  }

  getPoDrag(draftId) {
    this.companyService.purchaseOrder(draftId)
      .subscribe((purchaseOrder) => {
        this.purchaseOrder = purchaseOrder;
        this.getAdditionalData();
        this.purchaseOrderForm.get('poInfo').patchValue(purchaseOrder);
        purchaseOrder.line_items && purchaseOrder.line_items.forEach((li, index) => {
          const lineItemForm = this.formBuilder.group({
            product_id: [],
            product_name: [],
            quantity_ordered: [],
            unit_price: [],
            unit_price_currency: [],
            customer_part_id: [],
          });
          lineItemForm.patchValue(li);
          (this.purchaseOrderForm.get('poInfo').get('line_items') as FormArray).insert(index, lineItemForm);
        });
      });

  }

  getAdditionalData() {
    this.companyService.getPoFiles(this.purchaseOrder.id)
      .subscribe((files) => {
        this.poFiles = files;
      });

    this.purchaseOrderService.getSubscribers(this.purchaseOrder.id)
      .subscribe((subscribers) => {
        this.subscribers = subscribers;
      });

    this.companyService
      .getCheckList(this.organizationId, this.purchaseOrder.id)
      .subscribe((checklist) => {
        this.checklist = checklist[0];
      });
  }

  stepChange($event: StepperSelectionEvent, stepper) {
    this.selectedTabIndex = $event.selectedIndex;
    $event.selectedStep.interacted = false;
  }

  createPO() {
    // if start and cancel dates are the same, lets confirm with the user
    if (moment.utc(this.purchaseOrder.cancel_date).format() === moment.utc(this.purchaseOrder.order_date).format()) {
      if (!window.confirm('Order date and Cancel date are the same. Would you like to proceed anyway?')) {
        return;
      }
    }
    const purchaseOrder = {
      ...(this.purchaseOrderForm.get('poInfo') as FormGroup).getRawValue(),
      id: this.purchaseOrder.id,
      customer_id: this.purchaseOrder.customer_id,
      order_date: this.purchaseOrder.order_date,
      ship_date: this.purchaseOrder.ship_date,
      cancel_date: this.purchaseOrder.cancel_date,
      line_items: this.purchaseOrder.line_items,
      approver_id: this.purchaseOrder.approver_id,
      copy_receivers: this.purchaseOrder.copy_receivers,
      subscribers: this.purchaseOrder.subscribers,
      is_simple: false,
    };
    this.isSaving = true;
    this.purchaseOrderService.saveDraft(this.organizationId, purchaseOrder, true)
      .subscribe(
        po => {
          Swal.fire({
            title: 'Congratulations!',
            text: 'Your supplier has received the Purchase Order. A notification will be sent to you when it gets approved or rejected.',
            icon: 'success',
            showConfirmButton: false,
            timer: 5000,
          });
          setTimeout(() => {
            this.router.navigate(['customers', 'work-commissioned', this.purchaseOrder.id]);
            this.isSaving = false;
          }, 500);
        },
        error => {
          this.isSaving = false;
          const errMsg = !!error.error && !!error.error.detail ? error.error.detail : error.message;
          this.$toaster.show('error', 'Purchase Order', errMsg);
        },
      );
  }

  goBack() {
    this.location.back();
  }

  updateCheckList(checklist) {
    this.checklist = checklist;
  }

  savePODraft() {
    this.purchaseOrderService.saveDraft(this.organizationId, this.purchaseOrder)
      .subscribe((result) => {
        this.purchaseOrder = result;
        this.goBack();
      });
  }

  deletePoDraft() {
    if (!this.purchaseOrder) {
      this.goBack();
      return;
    }
    Swal.fire({
      title: 'Confirm Action',
      text: 'Do you really want to delete this Purchase Order draft?',
      icon: 'warning',
      showCancelButton: true,
      buttonsStyling: false,
      confirmButtonText: 'Yes, delete it!',
      customClass: {
        confirmButton: 'btn btn-primary mr-2',
        cancelButton: 'btn btn-danger ml-2',
      },
      confirmButtonColor: '#41b146',
      cancelButtonColor: '#c74141',

    }).then(async (result) => {
      if (result.value) {
        if (this.purchaseOrder && this.purchaseOrder.id) {
          this.purchaseOrderService.deleteDraft(this.organizationId, this.purchaseOrder.id).subscribe(() => {
            this.goBack();
          });
        } else {
          this.goBack();
        }
        return;
      }
    });
  }

  checklistChanged(checklist) {
    this.checklist = checklist;
  }
}
